import apiService from "~/services/service";
import API_ROUTES from "~/api-routes";
import type { ApiError } from "~/services/service";

/**
 * Extracts employee, entity, and tenant information from a blob URL path.
 *
 * Expected blob path pattern for ESS tax statements:
 * {tenant_id}/{entity_id}/{employee_id}/tax/{year}/{filename}
 *
 * @param blobUrl - The full blob URL or blob path
 * @returns Object containing extracted IDs or null if pattern doesn't match
 */
export const extractIdsFromBlobPath = (
  blobUrl: string,
): {
  tenantId: string;
  entityId: string;
  employeeId: string;
  year: string;
  fileName: string;
} | null => {
  try {
    // Handle both full URLs and relative paths
    const url = new URL(blobUrl);
    const pathParts = url.pathname.split("/").filter(Boolean);

    // Find the index of "ess" container if present
    const essIndex = pathParts.indexOf("ess");
    const startIndex = essIndex >= 0 ? essIndex + 1 : 0;

    // Expected pattern: tenant_id/entity_id/employee_id/tax/year/filename
    if (pathParts.length < startIndex + 6) {
      return null;
    }

    const tenantId = pathParts[startIndex];
    const entityId = pathParts[startIndex + 1];
    const employeeId = pathParts[startIndex + 2];
    const taxSegment = pathParts[startIndex + 3];
    const year = pathParts[startIndex + 4];
    const fileName = pathParts.slice(startIndex + 5).join("/");

    // Validate that we have the "tax" segment
    if (taxSegment !== "tax") {
      return null;
    }

    return {
      tenantId,
      entityId,
      employeeId,
      year,
      fileName,
    };
  } catch {
    // If URL parsing fails, try to parse as relative path
    const pathParts = blobUrl.split("/").filter(Boolean);

    // Expected pattern: tenant_id/entity_id/employee_id/tax/year/filename
    if (pathParts.length < 6) {
      return null;
    }

    const tenantId = pathParts[0];
    const entityId = pathParts[1];
    const employeeId = pathParts[2];
    const taxSegment = pathParts[3];
    const year = pathParts[4];
    const fileName = pathParts.slice(5).join("/");

    if (taxSegment !== "tax") {
      return null;
    }

    return {
      tenantId,
      entityId,
      employeeId,
      year,
      fileName,
    };
  }
};

/**
 * Payload structure for tax statement notification
 */
export interface TaxStatementNotificationPayload {
  blobUrl: string;
  filePath: string;
  tenantId: string;
  entityId: string;
  employeeId: string;
  year: string;
  fileName: string;
  category?: string;
  subCategory?: string;
  fileSize?: string;
  uploadedAt?: string;
  uploadedBy?: string;
}

/**
 * Creates a notification payload from blob URL and additional context.
 *
 * @param blobUrl - The complete blob URL
 * @param context - Additional context about the file upload
 * @returns Notification payload or null if extraction fails
 */
export const createNotificationPayload = (
  blobUrl: string,
  context: {
    category?: string;
    subCategory?: string;
    fileSize?: string;
    uploadedAt?: string;
    uploadedBy?: string;
  } = {},
): TaxStatementNotificationPayload | null => {
  const extractedIds = extractIdsFromBlobPath(blobUrl);

  if (!extractedIds) {
    return null;
  }

  // Extract the file path from the blob URL (relative path within container)
  const url = new URL(blobUrl);
  const pathParts = url.pathname.split("/").filter(Boolean);
  const essIndex = pathParts.indexOf("ess");
  const filePath =
    essIndex >= 0
      ? pathParts.slice(essIndex + 1).join("/")
      : `${extractedIds.tenantId}/${extractedIds.entityId}/${extractedIds.employeeId}/tax/${extractedIds.year}/${extractedIds.fileName}`;

  return {
    blobUrl,
    filePath,
    tenantId: extractedIds.tenantId,
    entityId: extractedIds.entityId,
    employeeId: extractedIds.employeeId,
    year: extractedIds.year,
    fileName: extractedIds.fileName,
    category: context.category,
    subCategory: context.subCategory,
    fileSize: context.fileSize,
    uploadedAt: context.uploadedAt || new Date().toISOString(),
    uploadedBy: context.uploadedBy,
  };
};

/**
 * Error handler for notification failures.
 * Logs error and can be extended to show toast notifications.
 *
 * @param error - The error that occurred
 * @param payload - The payload that failed to send
 */
export const handleNotificationError = (
  error: unknown,
  payload: TaxStatementNotificationPayload,
): void => {
  const apiError = error as ApiError;
  const errorMessage = apiError?.message || "Unknown error";
  const status = apiError?.status || "N/A";

  console.error("[Tax Statement Notification] Failed to notify employee:", {
    error: errorMessage,
    status,
    payload: {
      employeeId: payload.employeeId,
      fileName: payload.fileName,
      year: payload.year,
    },
  });

  // TODO: Add toast notification here if needed
  // Example:
  // toastRef.current?.show({
  //   severity: "warn",
  //   summary: "Notification Failed",
  //   detail: "Failed to send tax statement notification to employee.",
  // });
};

/**
 * Sends a notification to the employee about their tax statement upload.
 * This is a fire-and-forget operation that doesn't block the upload flow.
 *
 * @param payload - The notification payload
 * @returns Promise that resolves when notification is sent (or fails silently)
 */
export const notifyEmployeeTaxStatement = async (
  payload: TaxStatementNotificationPayload,
): Promise<void> => {
  try {
    console.log("Sending tax statement notification:", payload);
    await apiService(API_ROUTES.NOTIFY_EMPLOYEE_TAX_STATEMENT, "POST", {
      body: payload,
    });
  } catch (error) {
    handleNotificationError(error, payload);
    // Don't throw - this is fire-and-forget
  }
};

/**
 * Main function to handle tax statement notification after upload.
 * Extracts information from blob URL and sends notification.
 *
 * @param blobUrl - The complete blob URL of the uploaded file
 * @param context - Additional context about the upload
 */
export const handleTaxStatementUploadNotification = async (
  blobUrl: string,
  context: {
    category?: string;
    subCategory?: string;
    fileSize?: string;
    uploadedAt?: string;
    uploadedBy?: string;
  } = {},
): Promise<void> => {
  const payload = createNotificationPayload(blobUrl, context);

  if (!payload) {
    console.warn(
      "[Tax Statement Notification] Could not extract IDs from blob URL:",
      blobUrl,
    );
    return;
  }

  // Fire and forget - don't await to avoid blocking
  notifyEmployeeTaxStatement(payload).catch((error) => {
    console.error("Error sending tax statement notification:", error);
    // Error already handled in notifyEmployeeTaxStatement
  });
};
